import { Navigate, Outlet, type RouteObject } from "react-router";
import { AppShell } from "../components/layout/app-shell";
import { AppShellSkeleton } from "../components/layout/app-shell-skeleton";
import { FullPageLoader } from "../components/layout/full-page-loader";
import { ChangePasswordPage } from "../features/auth/change-password-page";
import { LoginPage } from "../features/auth/login-page";
import { RequireAuth, WELCOME_PATH } from "../features/auth/require-auth";
import { SystemAdminLoginPage } from "../features/auth/system-admin-login-page";
import { DashboardPage } from "../features/dashboard/dashboard-page";
import { NotFoundPage, RouteErrorPage } from "../features/errors/error-pages";
import {
  LOGIN_PATH,
  SET_PASSWORD_PATH,
  SIGNING_PATH,
  SYSTEM_ADMIN_LOGIN_PATH,
} from "../lib/sign-in-paths";

const platformLayout = async () => ({
  Component: (await import("../features/platform/platform-common")).PlatformLayout,
});

/**
 * Platform console (Eduleo's own team). Every page sits behind PlatformLayout, which lets in
 * only people whose /auth/me has platform capabilities. Each page then checks its own permission.
 */
const platformRoutes: RouteObject = {
  path: "platform",
  lazy: platformLayout,
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import("../features/platform/overview-page")).PlatformOverviewPage,
      }),
    },
    {
      path: "schools",
      lazy: async () => ({
        Component: (await import("../features/platform/schools-page")).SchoolsPage,
      }),
    },
    {
      path: "schools/new",
      lazy: async () => ({
        Component: (await import("../features/platform/onboard-school-page")).OnboardSchoolPage,
      }),
    },
    {
      path: "schools/:code",
      lazy: async () => ({
        Component: (await import("../features/platform/school-page")).SchoolPage,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import("../features/platform/school-overview-tab")).SchoolOverviewTab,
          }),
        },
        {
          path: "details",
          lazy: async () => ({
            Component: (await import("../features/platform/school-details-tab")).SchoolDetailsTab,
          }),
        },
        {
          path: "documents",
          lazy: async () => ({
            Component: (await import("../features/platform/school-documents-tab"))
              .SchoolDocumentsTab,
          }),
        },
        {
          path: "contracts",
          lazy: async () => ({
            Component: (await import("../features/platform/school-contracts-tab"))
              .SchoolContractsTab,
          }),
        },
        {
          path: "invoices",
          lazy: async () => ({
            Component: (await import("../features/platform/invoices-page")).SchoolInvoicesTab,
          }),
        },
        {
          path: "accounts",
          lazy: async () => ({
            Component: (await import("../features/platform/school-accounts-tab")).SchoolAccountsTab,
          }),
        },
        {
          path: "accounts/new",
          lazy: async () => ({
            Component: (await import("../features/platform/school-account-form-page"))
              .SchoolAccountFormPage,
          }),
        },
        {
          path: "accounts/:userId/edit",
          lazy: async () => ({
            Component: (await import("../features/platform/school-account-form-page"))
              .SchoolAccountFormPage,
          }),
        },
      ],
    },
    {
      path: "invoices",
      lazy: async () => ({
        Component: (await import("../features/platform/invoices-page")).InvoicesPage,
      }),
    },
    {
      path: "invoices/new",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-form-page")).InvoiceFormPage,
      }),
    },
    {
      path: "invoices/:id",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-page")).InvoicePage,
      }),
    },
    {
      path: "invoices/:id/edit",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-form-page")).InvoiceFormPage,
      }),
    },
    {
      path: "team",
      lazy: async () => ({
        Component: (await import("../features/platform/team-page")).TeamPage,
      }),
    },
    {
      path: "team/new",
      lazy: async () => ({
        Component: (await import("../features/platform/team-member-form-page")).TeamMemberFormPage,
      }),
    },
    {
      path: "team/:id",
      lazy: async () => ({
        Component: (await import("../features/platform/team-member-page")).TeamMemberPage,
      }),
    },
    {
      path: "team/:id/edit",
      lazy: async () => ({
        Component: (await import("../features/platform/team-member-form-page")).TeamMemberFormPage,
      }),
    },
    {
      path: "regions",
      lazy: async () => ({
        Component: (await import("../features/platform/regions-page")).RegionsPage,
      }),
    },
    {
      path: "regions/new",
      lazy: async () => ({
        Component: (await import("../features/platform/regions-page")).RegionFormPage,
      }),
    },
    {
      path: "regions/:id/edit",
      lazy: async () => ({
        Component: (await import("../features/platform/regions-page")).RegionFormPage,
      }),
    },
    {
      path: "settings",
      lazy: async () => ({
        Component: (await import("../features/platform/settings-layout")).SettingsLayout,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import("../features/platform/profile-page")).ProfileSettingsPage,
          }),
        },
        {
          path: "notifications",
          lazy: async () => ({
            Component: (await import("../features/platform/notification-settings-page"))
              .NotificationSettingsPage,
          }),
        },
        {
          path: "security",
          lazy: async () => ({
            Component: (await import("../features/platform/security-page")).SecurityPage,
          }),
        },
        {
          path: "company",
          lazy: async () => ({
            Component: (await import("../features/platform/company-page")).CompanyPage,
          }),
        },
      ],
    },
    // Where these pages were before System settings; links in old emails still work.
    { path: "security", element: <Navigate to="/platform/settings/security" replace /> },
    { path: "company", element: <Navigate to="/platform/settings/company" replace /> },
  ],
};

/** Contract and invoice print views: a clean A4 page, without the app's menus. */
const platformPrintRoutes: RouteObject = {
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  hydrateFallbackElement: <FullPageLoader />,
  errorElement: <RouteErrorPage />,
  children: [
    {
      lazy: platformLayout,
      children: [
        {
          path: "/platform/contracts/:id/print",
          lazy: async () => ({
            Component: (await import("../features/platform/contract-print-page")).ContractPrintPage,
          }),
        },
        {
          path: "/platform/invoices/:id/print",
          lazy: async () => ({
            Component: (await import("../features/platform/invoice-print-page")).InvoicePrintPage,
          }),
        },
      ],
    },
  ],
};

/**
 * Shared by the browser router and the tests' memory router.
 * Sign-in and Home load with the app; every other module loads when first opened,
 * so a parent on a slow phone doesn't download screens they will never use.
 */
/** A lazily loaded page: its code downloads the first time it's opened. */
function settingsPage<M extends Record<string, unknown>>(
  path: string,
  load: () => Promise<M>,
  name: keyof M & string,
) {
  return [
    {
      path,
      lazy: async () => ({ Component: (await load())[name] as React.ComponentType }),
    },
  ];
}

export const routes: RouteObject[] = [
  // A school's signatory signs Eduleo's agreement from an emailed link: no sign-in, no app shell.
  {
    path: SIGNING_PATH,
    lazy: async () => ({
      Component: (await import("../features/signing/signing-page")).SigningPage,
    }),
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
  },
  // Opened from an emailed "set your password" link: no sign-in, no app shell.
  {
    path: SET_PASSWORD_PATH,
    lazy: async () => ({
      Component: (await import("../features/auth/set-password-page")).SetPasswordPage,
    }),
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
  },
  { path: LOGIN_PATH, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // Eduleo's own team signs in here; the school sign-in doesn't mention it.
  {
    path: SYSTEM_ADMIN_LOGIN_PATH,
    element: <SystemAdminLoginPage />,
    errorElement: <RouteErrorPage />,
  },
  // Outside RequireAuth on purpose: it is where RequireAuth sends people who must change it.
  { path: "/change-password", element: <ChangePasswordPage />, errorElement: <RouteErrorPage /> },
  // A family completing their child's details before the school opens the app: signed in,
  // but without the app's menus (nothing else works for them yet).
  {
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: WELCOME_PATH,
        lazy: async () => ({
          Component: (await import("../features/portal/welcome-page")).WelcomePage,
        }),
      },
    ],
  },
  platformPrintRoutes,
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    // Shown while a page's code downloads on the first visit, instead of a blank screen.
    hydrateFallbackElement: <AppShellSkeleton />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      ...settingsPage(
        "students",
        () => import("../features/students/students-page"),
        "StudentsPage",
      ),
      ...settingsPage(
        "students/new",
        () => import("../features/students/admission-form-page"),
        "AdmissionFormPage",
      ),
      {
        // A student's page; each tab has its own address.
        path: "students/:id",
        lazy: async () => ({
          Component: (await import("../features/students/student-details-page")).StudentDetailsPage,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("../features/students/student-details-page"))
                .StudentOverviewTab,
            }),
          },
          {
            path: "family",
            lazy: async () => ({
              Component: (await import("../features/students/student-family-tab")).StudentFamilyTab,
            }),
          },
          {
            path: "details",
            lazy: async () => ({
              Component: (await import("../features/students/student-info-tab")).StudentInfoTab,
            }),
          },
          {
            path: "documents",
            lazy: async () => ({
              Component: (await import("../features/students/student-documents-tab"))
                .StudentDocumentsTab,
            }),
          },
          {
            path: "history",
            lazy: async () => ({
              Component: (await import("../features/students/student-history-tab"))
                .StudentHistoryTab,
            }),
          },
        ],
      },
      ...settingsPage(
        "students/:id/edit",
        () => import("../features/students/student-edit-page"),
        "StudentEditPage",
      ),
      ...settingsPage(
        "students/:id/invite",
        () => import("../features/portal/portal-pages"),
        "InvitePage",
      ),
      ...settingsPage(
        "students/:id/request-change",
        () => import("../features/portal/portal-pages"),
        "RequestChangePage",
      ),
      ...settingsPage(
        "students/:id/people/new",
        () => import("../features/students/person-form-page"),
        "PersonFormPage",
      ),
      ...settingsPage(
        "students/:id/people/:personId/edit",
        () => import("../features/students/person-form-page"),
        "PersonFormPage",
      ),
      ...settingsPage(
        "settings/admission",
        () => import("../features/settings/admission-settings-page"),
        "AdmissionSettingsPage",
      ),
      ...settingsPage(
        "settings/admission/questions/new",
        () => import("../features/settings/custom-question-page"),
        "CustomQuestionPage",
      ),
      ...settingsPage(
        "settings/admission/questions/:id/edit",
        () => import("../features/settings/custom-question-page"),
        "CustomQuestionPage",
      ),
      ...settingsPage(
        "settings/classes",
        () => import("../features/settings/classes-page"),
        "ClassesPage",
      ),
      ...settingsPage(
        "settings/classes/new",
        () => import("../features/settings/class-form-page"),
        "ClassFormPage",
      ),
      ...settingsPage(
        "settings/classes/years",
        () => import("../features/settings/academic-years-page"),
        "AcademicYearsPage",
      ),
      ...settingsPage(
        "settings/classes/years/new",
        () => import("../features/settings/academic-years-page"),
        "YearFormPage",
      ),
      ...settingsPage(
        "settings/classes/years/:id/edit",
        () => import("../features/settings/academic-years-page"),
        "YearFormPage",
      ),
      ...settingsPage(
        "settings/classes/:id",
        () => import("../features/settings/class-details-page"),
        "ClassDetailsPage",
      ),
      ...settingsPage(
        "settings/classes/:id/edit",
        () => import("../features/settings/class-form-page"),
        "ClassFormPage",
      ),
      ...settingsPage(
        "settings/classes/:id/sections/new",
        () => import("../features/settings/class-form-page"),
        "SectionFormPage",
      ),
      ...settingsPage(
        "settings/classes/:id/sections/:sectionId/edit",
        () => import("../features/settings/class-form-page"),
        "SectionFormPage",
      ),
      ...settingsPage(
        "settings/classes/:id/sections/:sectionId/seating",
        () => import("../features/settings/seating-plan-page"),
        "SectionSeatingPage",
      ),
      ...settingsPage(
        "settings/classes/:id/seating",
        () => import("../features/settings/seating-plan-page"),
        "ClassSeatingPage",
      ),
      ...settingsPage(
        "settings/people",
        () => import("../features/settings/staff-page"),
        "StaffPage",
      ),
      ...settingsPage(
        "settings/people/staff/new",
        () => import("../features/settings/staff-form-page"),
        "StaffFormPage",
      ),
      ...settingsPage(
        "settings/people/staff/:id",
        () => import("../features/settings/staff-details-page"),
        "StaffDetailsPage",
      ),
      ...settingsPage(
        "settings/people/staff/:id/edit",
        () => import("../features/settings/staff-form-page"),
        "StaffFormPage",
      ),
      ...settingsPage(
        "settings/people/import",
        () => import("../features/settings/staff-import-page"),
        "StaffImportPage",
      ),
      ...settingsPage(
        "settings/people/roles",
        () => import("../features/settings/roles-page"),
        "RolesPage",
      ),
      ...settingsPage(
        "settings/people/roles/compare",
        () => import("../features/settings/roles-compare-page"),
        "RolesComparePage",
      ),
      ...settingsPage(
        "settings/people/roles/new",
        () => import("../features/settings/role-form-page"),
        "RoleFormPage",
      ),
      ...settingsPage(
        "settings/people/roles/:id",
        () => import("../features/settings/role-details-page"),
        "RoleDetailsPage",
      ),
      ...settingsPage(
        "settings/people/roles/:id/edit",
        () => import("../features/settings/role-form-page"),
        "RoleFormPage",
      ),
      platformRoutes,
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
